import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ControlButton,
  Controls,
  ReactFlow,
  useNodesState,
  useStore,
  type EdgeMouseHandler,
  type EdgeTypes,
  type NodeMouseHandler,
  type NodeTypes,
  type ReactFlowInstance,
  type SelectionDragHandler,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useChainStore } from "../store/chainStore";
import { useLayoutStore, type XY } from "../store/layoutStore";
import { SystemNode } from "./SystemNode";
import { FloatingEdge } from "./FloatingEdge";
import { SystemContextMenu } from "./SystemContextMenu";
import { ConnectionContextMenu } from "./ConnectionContextMenu";
import { AutoArrangeConfirmModal } from "./AutoArrangeConfirmModal";
import { connectionsToEdges, systemsToNodes } from "../graph/chainToFlow";
import type { SystemFlowNode, SystemNodeData } from "../graph/types";
import { useChainLayout } from "../graph/useChainLayout";
import { useAutoArrange } from "../graph/useAutoArrange";
import { useLayoutSaver } from "../graph/useLayoutSaver";
import { useActiveChainId } from "../hooks/useActiveChainId";
import { useChain } from "../hooks/useChain";
import {
  useStaticSystemsById,
  useWhDestinationByCode,
} from "../hooks/useStaticSystems";
import { useCurrentSystem } from "../hooks/useCurrentSystem";
import { usePresence } from "../hooks/usePresence";
import { TRADE_HUB_NAMES, tradeHubDisplay } from "../utils/closestHub";

type MenuState =
  | { kind: "system"; id: string; x: number; y: number }
  | { kind: "connection"; id: string; x: number; y: number }
  | null;

const nodeTypes: NodeTypes = { system: SystemNode };
const edgeTypes: EdgeTypes = { floating: FloatingEdge };

export function ChainGraph() {
  const systems = useChainStore((s) => s.systems);
  const connections = useChainStore((s) => s.connections);
  const [chainId] = useActiveChainId();
  const chainQuery = useChain(chainId);

  const positions = useLayoutStore((s) => s.positions);
  const setHighlightedSystemId = useLayoutStore((s) => s.setHighlightedSystemId);
  const setPosition = useLayoutStore((s) => s.setPosition);
  const setPositions = useLayoutStore((s) => s.setPositions);
  const markDragging = useLayoutStore((s) => s.markDragging);
  const clearDragging = useLayoutStore((s) => s.clearDragging);
  const { enqueue } = useLayoutSaver();

  const rfRef = useRef<ReactFlowInstance<SystemFlowNode> | null>(null);
  const onLayoutReset = useCallback(() => {
    requestAnimationFrame(() => rfRef.current?.fitView());
  }, []);
  useChainLayout(onLayoutReset);

  const [rfNodes, setRfNodes, onNodesChange] = useNodesState<SystemFlowNode>([]);

  const { byId: staticById } = useStaticSystemsById();
  const { forCode: destinationFor } = useWhDestinationByCode();
  const currentSystemQuery = useCurrentSystem();
  const currentSystemId = currentSystemQuery.data?.id ?? null;
  const presenceQuery = usePresence(chainId);
  const presenceBySystem = presenceQuery.data;

  const augmentedSystems = useMemo<SystemNodeData[]>(
    () =>
      systems.map((s) => {
        const staticSys = staticById(s.id);
        const name = staticSys?.name ?? s.id;
        const systemClass = staticSys?.class ?? "C1";
        const statics = staticSys?.statics ?? [];
        const effect = staticSys?.effect ?? null;
        const isTradeHub = staticSys
          ? TRADE_HUB_NAMES.has(staticSys.name)
          : false;
        const hubInfo =
          (systemClass === "HS" || systemClass === "LS") && !isTradeHub
            ? tradeHubDisplay(staticSys?.hub_jumps)
            : null;
        const isCurrent = s.id === currentSystemId;
        const isShattered = staticSys?.is_shattered ?? false;
        const presence = presenceBySystem?.[s.id] ?? [];
        return {
          ...s,
          name,
          class: systemClass,
          statics,
          effect,
          isTradeHub,
          hubInfo,
          isCurrent,
          isShattered,
          presence,
        };
      }),
    [systems, staticById, currentSystemId, presenceBySystem],
  );

  useEffect(() => {
    setRfNodes((current) => {
      const byId = new Map<string, SystemFlowNode>();
      for (const n of current) byId.set(n.id, n);
      const next = systemsToNodes(augmentedSystems, positions);
      return next.map((n) => {
        const prev = byId.get(n.id);
        if (!prev) return n;
        const carry: Partial<SystemFlowNode> = {};
        if (prev.selected) carry.selected = true;
        if (prev.dragging) carry.position = prev.position;
        if (Object.keys(carry).length === 0) return n;
        return { ...n, ...carry };
      });
    });
  }, [augmentedSystems, positions, setRfNodes]);

  useEffect(() => {
    let count = 0;
    let onlyId: string | null = null;
    for (const n of rfNodes) {
      if (n.selected) {
        count++;
        if (count > 1) {
          onlyId = null;
          break;
        }
        onlyId = n.id;
      }
    }
    setHighlightedSystemId(count === 1 ? onlyId : null);
  }, [rfNodes, setHighlightedSystemId]);

  const rfEdges = useMemo(
    () => connectionsToEdges(connections, systems, destinationFor),
    [connections, systems, destinationFor],
  );

  const handleAutoArrange = useAutoArrange(rfNodes);

  const chainAccess = chainQuery.data?.access ?? "private";
  const [confirmArrange, setConfirmArrange] = useState(false);
  const requestAutoArrange = useCallback(() => {
    if (chainAccess === "private") {
      handleAutoArrange();
    } else {
      setConfirmArrange(true);
    }
  }, [chainAccess, handleAutoArrange]);

  const [menu, setMenu] = useState<MenuState>(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  const onNodeContextMenu: NodeMouseHandler = (e, n) => {
    e.preventDefault();
    setMenu({ kind: "system", id: n.id, x: e.clientX, y: e.clientY });
  };
  const onEdgeContextMenu: EdgeMouseHandler = (e, edge) => {
    e.preventDefault();
    setMenu({ kind: "connection", id: edge.id, x: e.clientX, y: e.clientY });
  };
  const onPaneContextMenu = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      e.preventDefault();
      setMenu(null);
    },
    [],
  );

  const onNodeDragStart: NodeMouseHandler = (_, n) => {
    markDragging(n.id);
  };

  const onNodeDragStop: NodeMouseHandler = (_, n) => {
    setPosition(n.id, n.position);
    enqueue({ [n.id]: n.position });
    clearDragging(n.id);
  };

  const onSelectionDragStart = useCallback<SelectionDragHandler>(
    (_, nodes) => {
      for (const n of nodes) markDragging(n.id);
    },
    [markDragging],
  );

  const onSelectionDragStop = useCallback<SelectionDragHandler>(
    (_, nodes) => {
      const updates: Record<string, XY> = {};
      for (const n of nodes) updates[n.id] = n.position;
      setPositions(updates);
      enqueue(updates);
      for (const n of nodes) clearDragging(n.id);
    },
    [setPositions, enqueue, clearDragging],
  );

  return (
    <>
      {systems.length === 0 ? (
        <div className="empty">
          <p>
            {chainId == null
              ? "Pick a chain from the switcher, or create one to start tracking."
              : chainQuery.isLoading
                ? "Loading chain…"
                : chainQuery.isError
                  ? "Could not load chain."
                  : "Pick a chain from the switcher, or create one to start tracking."}
          </p>
        </div>
      ) : (
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onInit={(instance) => {
            rfRef.current = instance;
          }}
          onNodesChange={onNodesChange}
          onNodeDragStart={onNodeDragStart}
          onNodeDragStop={onNodeDragStop}
          onSelectionDragStart={onSelectionDragStart}
          onSelectionDragStop={onSelectionDragStop}
          onNodeContextMenu={onNodeContextMenu}
          onEdgeContextMenu={onEdgeContextMenu}
          onPaneContextMenu={onPaneContextMenu}
          fitView
        >
          <Controls>
            <AutoArrangeButton
              selectionCount={rfNodes.filter((n) => n.selected).length}
              onClick={requestAutoArrange}
            />
          </Controls>
        </ReactFlow>
      )}
      {menu?.kind === "system" && (
        <SystemContextMenu
          systemId={menu.id}
          x={menu.x}
          y={menu.y}
          onClose={closeMenu}
        />
      )}
      {menu?.kind === "connection" && (
        <ConnectionContextMenu
          connectionId={menu.id}
          x={menu.x}
          y={menu.y}
          onClose={closeMenu}
        />
      )}
      <AutoArrangeConfirmModal
        open={confirmArrange}
        onOpenChange={setConfirmArrange}
        access={chainAccess}
        onConfirm={() => {
          setConfirmArrange(false);
          handleAutoArrange();
        }}
      />
    </>
  );
}

interface AutoArrangeButtonProps {
  selectionCount: number;
  onClick: () => void;
}

function AutoArrangeButton({ selectionCount, onClick }: AutoArrangeButtonProps) {
  const interactive = useStore((s) => s.nodesDraggable);
  const label =
    selectionCount > 1 ? "Auto-arrange selection" : "Auto-arrange";
  return (
    <ControlButton
      title={interactive ? label : `${label} (unlock to enable)`}
      onClick={onClick}
      disabled={!interactive}
    >
      ↻
    </ControlButton>
  );
}
